// frontend/src/components/McpConnections/ConnectionItem.js
import { useState } from 'react';
import { FaTrash, FaChevronDown, FaChevronUp, FaCheck } from 'react-icons/fa';
import { FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';
import ToolItem from './ToolItem';
import Spinner from './Spinner';
import { reauthenticateOauth, updateCustomHeaders } from './api';
import { DataState } from '../../context/DataProvider';
import { BsPuzzle } from "react-icons/bs";
import { MdOutlineAdd, MdClose } from "react-icons/md";


const StatusIndicator = ({ status }) => {
  const statusConfig = {
    valid: { color: 'bg-green-500', label: 'Valid' },
    invalid: { color: 'bg-red-500', label: 'Invalid' },
    invalid_auth: { color: 'bg-yellow-500', label: 'Authentication Invalid' },
    unchecked: { color: 'bg-gray-500', label: 'Unchecked' },
  };
  const config = statusConfig[status] || statusConfig.unchecked;
  return <div className={`w-3 h-3 rounded-full ${config.color}`} title={`Status: ${config.label}`} />;
};

const ConnectionItem = ({ connection, isExpanded, onToggle, onDelete, onRecheck }) => {
  const {userGoogleId:accessToken} = DataState();
  const [expandedToolName, setExpandedToolName] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  
  // State for the reconnect flow
  const [showReconnectForm, setShowReconnectForm] = useState(false);
  const [newHeaders, setNewHeaders] = useState('{\n  "Authorization": "Bearer YOUR_NEW_TOKEN"\n}');
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleToggleTool = (toolName) => {
    setExpandedToolName(prevName => (prevName === toolName ? null : toolName));
  };
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this connection?')) {
      setIsDeleting(true);
      await onDelete(connection.id);
    }
  };

  const handleRecheck = async () => {
    setIsRechecking(true);
    await onRecheck(connection.id);
    setIsRechecking(false);
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    if (connection.auth_type === 'oauth2') {
        try {
            const response = await reauthenticateOauth(accessToken, connection.id);
            window.open(response.authorization_url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            alert('Could not start re-authentication flow. Please check console.');
            console.error(error);
        }
    } else if (connection.auth_type === 'custom_header') {
        setShowReconnectForm(true);
    }
    setIsReconnecting(false);
  };

  const handleUpdateHeaders = async () => {
      setIsReconnecting(true);
      try {
          const parsedHeaders = JSON.parse(newHeaders);
          await updateCustomHeaders(accessToken, connection.id, parsedHeaders);
          setShowReconnectForm(false);
          await onRecheck(connection.id); // Recheck to confirm new status
      } catch (error) {
          alert('Failed to update headers. Ensure JSON is valid.');
      } finally {
          setIsReconnecting(false);
      }
  };

  return (
    <>
      <div 
      className="flex w-fit max-w-xl flex-wrap items-center border border-zinc-400 rounded-full gap-2 p-1 px-2  bg-white dark:bg-zinc-800 transition-all duration-300"
      title={connection?.sse_url}
      >

        <div className="bg-zinc-200 rounded-full p-1 cursor-pointer">
          <BsPuzzle className="w-4 h-4 text-zinc-700" />
        </div>


        <div className="flex flex-col justify-center ml-1 text-zinc-800 dark:text-gray-200 text-sm">
          <span className="font-semibold truncate max-w-[140px]">
            {connection.connection_name}
          </span>
          {connection.sse_url && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[160px]">
              {connection.sse_url}
            </span>
          )}
        </div>

        {/* Status */}
        <StatusIndicator status={connection.connection_status} />

        {/* Recheck */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRecheck();
          }}
          className="bg-zinc-100 dark:bg-zinc-600 hover:bg-zinc-300 dark:hover:bg-zinc-500 transition-all duration-300 p-2 rounded-full"
        >
          <FiRefreshCw size={10} />
        </button>

          {/* Expand */}
        <button
          onClick={onToggle}
          className="bg-zinc-100 dark:bg-zinc-600 hover:bg-zinc-300 dark:hover:bg-zinc-500 transition-all duration-300 p-2 rounded-full"
        >
          {isExpanded ? <FaChevronUp size={10}/> : <FaChevronDown size={10}/>}
        </button>
        

        {/* Delete */}
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          className="bg-green-700 hover:bg-green-800 transition-all duration-300 p-2 rounded-full text-xs"
        >
          <FaCheck size={10} />
          </button>
      </div>




      {/* --- Modal for Expanded Section --- */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }} // Close on background click

        >
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 w-full max-w-xl ">
          <div
            className=" relative max-h-[60vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} 
          >
            {connection.connection_status === 'invalid_auth' ? (
              <div className="text-yellow-300">
                <div className="flex items-center gap-3 mb-4">
                  <FiAlertTriangle className="text-yellow-400 text-xl" />
                  <div>
                    <p className="font-semibold">Authentication Failed</p>
                    <p className="text-xs">{connection.error_message}</p>
                  </div>
                </div>

                {!showReconnectForm && (
                  <button
                    onClick={handleReconnect}
                    disabled={isReconnecting}
                    className="mb-4 px-3 py-2 bg-yellow-600 text-white font-semibold rounded-md hover:bg-yellow-700"
                  >
                    {isReconnecting ? <Spinner /> : 'Reconnect'}
                  </button>
                )}

                {showReconnectForm && connection.auth_type === 'custom_header' && (
                  <div className="space-y-3">
                    <label className="text-xs font-medium text-gray-400">Enter New Headers (JSON)</label>
                    <textarea
                      value={newHeaders}
                      onChange={(e) => setNewHeaders(e.target.value)}
                      rows="4"
                      className="w-full font-mono text-xs bg-zinc-900 border border-zinc-600 rounded-md px-3 py-2 text-white"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateHeaders}
                        disabled={isReconnecting}
                        className="px-3 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700"
                      >
                        {isReconnecting ? 'Saving...' : 'Save New Headers'}
                      </button>
                      <button
                        onClick={() => setShowReconnectForm(false)}
                        className="px-3 py-2 bg-zinc-600 text-white font-semibold rounded-md hover:bg-zinc-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-white">
                {connection.connection_status === 'invalid' && (
                  <div className="mb-4 p-3 bg-red-900/50 text-red-300 rounded-md text-xs">
                    <strong>Connection Error:</strong> {connection.error_message}
                  </div>
                )}

                <h4 className="text-base font-semibold text-gray-300 mb-2">Available Tools</h4>

                {connection.tools?.length > 0 ? (
                  <ul className="space-y-4">
                    {connection.tools.map((tool) => (
                      <ToolItem
                        key={tool.name}
                        tool={tool}
                        connectionId={connection.id}
                        isExpanded={expandedToolName === tool.name}
                        onToggle={() => handleToggleTool(tool.name)}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No tools available for this connection.</p>
                )}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConnectionItem;